import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { useNetworkMetadata } from "Flocknote/apiHooks";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";

import {
  FnrPopoverOption,
  FnrPopoverOptionIcon,
  FnrPopoverOptionsList,
} from "UI/FnrPopover2";
import { colors } from "UI/colors";

import { countryOptions } from "Utilities/Address/networkCountryOptions";
import { useOnClickOutside } from "Utilities/Hooks/useOnClickOutside";

import { useAddress } from "./AddressContext";

CountryField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // country ID or country code abbreviation
  onChange: PropTypes.func,
  onMenuChange: PropTypes.func,
  flagFontSize: PropTypes.string,
  showOpenIndicator: PropTypes.bool,
  hideInternalFocusStyles: PropTypes.bool,
  style: PropTypes.object,
};

export function CountryField({
  value,
  onChange,
  onMenuChange,
  flagFontSize,
  hideChevron,
  showOpenIndicator = true,
  hideInternalFocusStyles = false,
  style,
}) {
  const [networkMetadata] = useNetworkMetadata();
  const networkCountry = networkMetadata?.address?.countryCode;

  const [menuOpen, setMenuOpen] = useState(false);
  const buttonRef = useRef();
  const popoverRef = useRef();
  const { clearStateField } = useAddress();
  useOnClickOutside(popoverRef, (e) => {
    // close the country select popover if user clicks outside while menu is open
    if (menuOpen) setMenuOpen(false);
  });

  let countryID = value;
  let selectedCountry =
    countryOptions.find((c) => c.ID === value) ||
    countryOptions.find((c) => c.countryCode === value);

  // the default country is the network's country
  if (!selectedCountry) {
    selectedCountry = countryOptions.find(
      (c) => c.countryCode === networkCountry
    );
  }

  if (!countryID) {
    // value prop is a countryCode, NOT an ID
    countryID = selectedCountry?.ID;
  }

  useEffect(() => {
    if (onMenuChange) onMenuChange(menuOpen);
  }, [menuOpen, onMenuChange]);

  const toggleMenuOpen = () => {
    setMenuOpen((prev) => !prev);
  };

  const handleOptionSelect = (id) => {
    if (id !== countryID) {
      onChange(id);
      clearStateField();
    }
    setMenuOpen(false);
  };

  return (
    <Wrapper style={style} className="country-field">
      <Button
        data-cy="FnrAddress_ContryFieldButton"
        role="button"
        tabIndex={0}
        ref={buttonRef}
        onClick={toggleMenuOpen}
        flagFontSize={flagFontSize}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            toggleMenuOpen();
          }
          if (e.key === "Escape") {
            setMenuOpen(false);
          }
        }}
        hideInternalFocusStyles={hideInternalFocusStyles}
      >
        {selectedCountry?.flag || "🇺🇸"}
        {showOpenIndicator ? <FontAwesomeIcon icon={faChevronDown} /> : null}
      </Button>

      <FnrPopoverOptionsList
        show={menuOpen}
        onShouldClose={() => setMenuOpen(false)}
        buttonRef={buttonRef}
      >
        <OptionsList ref={popoverRef}>
          {countryOptions.map((option) => {
            const isSelected = option.ID === countryID;
            return (
              <FnrPopoverOption
                key={option.ID}
                onClick={() => handleOptionSelect(option.ID)}
              >
                <FnrPopoverOptionIcon icon={isSelected ? "check fas" : ""} />
                {option.name}
              </FnrPopoverOption>
            );
          })}
        </OptionsList>
      </FnrPopoverOptionsList>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 68px;
  flex-shrink: 0;
  color: #222;
  border-right: 1px solid #e5e5e5;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 150ms;
  border-radius: 8px 0 0 8px;

  &:hover {
    background-color: #efefef;
  }
`;

const Button = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: ${(props) => props?.flagFontSize || "48px"};

  svg {
    margin-top: -5px;
    font-size: 19px;
    color: ${colors.fnBlue};
  }

  &:focus-visible {
    outline: ${(props) => (props.hideInternalFocusStyles ? "none" : "auto")};
  }
`;

const OptionsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;
